"use client"

import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { MriPackageCards } from "@/components/ui/mri-package-cards"
import { QuizCtaBanner } from "@/components/marketing/quiz-cta-banner"
import { CtaBanner } from "@/components/ui/cta-banner"
import { BookingCta } from "@/components/ui/booking-cta"
import { ComingSoonPill } from "@/components/ui/coming-soon-pill"
import { isBookingOpen, isComingSoon } from "@/i18n/config"
import { MapPinIcon, CheckIcon } from "@/components/ui/icons"
import { ValuePropCards } from "@/components/ui/value-prop-cards"
import { ReportMockupIpad } from "@/components/ui/report-mockup-ipad"
import { GoogleRating } from "@/components/ui/google-rating"
import { HeroBiomarkerImage } from "@/components/ui/hero-biomarker-image"
import { useMriHeroReadings } from "@/components/marketing/use-mri-hero-readings"
import { ServicesCarousel } from "@/components/ui/services-carousel"
import { useTranslatedServices } from "@/lib/use-translated-services"
import { applyServicesConfig } from "@/lib/services-overlay"
import { PhoneWalkthrough } from "@/components/ui/phone-walkthrough"
import { goKeys as k } from "@/i18n/go-keys"
import { homeKeys as hk } from "@/i18n/home-keys"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { useLivePrices } from "@/lib/use-live-prices"
import type { ReviewerExpert } from "@/lib/reviewer-experts"
import type { Testimonial, CmsServiceConfig } from "@/lib/cms"
import { BiomarkerDiseaseExplorer } from "@/components/ui/biomarker-disease-explorer"
import { InsuranceSection } from "@/components/ui/insurance-section"
import { PartnerLogoMarquee } from "@/components/ui/partner-logo-marquee"
import { notFound } from "next/navigation"
import { DoctorsSection } from "../../../_components/doctors-section"
import { TestimonialsSection } from "../../../_components/testimonials-section"
import { FaqSection } from "../../../_components/faq-section"
import { blobMediaUrl } from "@/lib/blob-url"

/* ─── Trust features ─── */
const TRUST_ITEMS = [
  k.trustMriReviewed,
  k.trustMriConditions,
  k.trustMriFollowUp,
  k.trustMriFastResults,
]

/* ─── Couples bullets ─── */
const COUPLES_BULLETS = [k.couplesBullet1, k.couplesBullet2, k.couplesBullet3]

/* ═══════════════════════════════════════════════════════
   PAGE COMPONENT
   ═══════════════════════════════════════════════════════ */

export default function FullbodymriPage({
  initialExperts = [],
  initialTestimonials = [],
  servicesConfig = [],
}: {
  initialExperts?: ReviewerExpert[]
  initialTestimonials?: Testimonial[]
  /** CMS `products` control plane (per-country status + order), joined by slug
   *  onto the carousel. Empty = ungated render + a Sentry alert from the loader
   *  (fail-loud). */
  servicesConfig?: CmsServiceConfig[]
}) {
  const t = useTranslations("go")
  const ht = useTranslations("home")
  const ut = useTranslations("ui")
  const heroReadings = useMriHeroReadings()
  const rpt = useTranslations("report")
  const lp = useLocalePath()
  const { code: country } = useCountry()
  const prices = useLivePrices(country)
  const mriComingSoon = isComingSoon(country, "mri")

  // Render where MRI is bookable, and in `checkup` markets where it's still
  // coming-soon (the hero/couples CTAs below switch to the join-waitlist flow
  // via `mriComingSoon`). 404 only where MRI doesn't exist yet — waitlist-only
  // / disabled markets. AHD-7869.
  if (!isBookingOpen(country, "mri") && !isComingSoon(country, "mri"))
    notFound()

  const experts = initialExperts
  // CMS control plane is the sole driver of carousel gating + order; empty
  // config = ungated passthrough (loader fires Sentry — fail-loud).
  const translatedServices = applyServicesConfig(
    useTranslatedServices(),
    servicesConfig,
    country
  )

  return (
    <>
      {/* ─── 1. Hero ─── */}
      <section className="mx-auto max-w-[1274px] px-10 pt-8 pb-5 md:pt-12 md:pb-8">
        <div className="flex flex-col items-center gap-8 md:flex-row md:gap-16">
          {/* Hero renders without entrance motion so the H1 is the LCP
              element and there's no awkward slide-in on first paint. */}
          <div className="flex-1 text-left">
            <div className="mb-4">
              <GoogleRating />
            </div>
            <h1 className="type-display-lg text-foreground max-w-3xl leading-[0.9] text-balance">
              {t(k.fullbodymriHeroHeading)}
            </h1>
            <p className="text-foreground mt-4 max-w-[460px] text-base leading-[1.3]">
              {t(k.fullbodymriHeroBody)}
            </p>
            {mriComingSoon && (
              <ComingSoonPill label={ut("comingSoon")} className="mt-6" />
            )}
            <div className="mt-8 flex flex-wrap gap-4">
              <BookingCta
                category="mri"
                trackingLabel="Hero book MRI"
                trackingLocation="fullbodymri-hero"
                className="bg-aubergine-800 inline-flex items-center justify-center rounded-full px-8 py-3 font-medium text-white transition-colors hover:bg-[#5a2a5d]"
              >
                {mriComingSoon ? ut("joinWaitlist") : t(k.fullbodymriHeroCta1)}
              </BookingCta>
              <Link
                href={lp("/how-it-works")}
                className="hover:text-aubergine-800 type-body-md text-foreground inline-flex items-center gap-2 px-4 py-2 transition-colors"
              >
                {t(k.fullbodymriHeroCta2)} &rarr;
              </Link>
            </div>
            <p className="mt-4 flex items-center gap-1.5 text-sm text-neutral-500">
              <MapPinIcon width={16} height={16} className="flex-shrink-0" />
              {t(k.fullbodymriHeroLocation)}
            </p>
          </div>
          <div className="w-full flex-shrink-0 md:w-[567px]">
            <HeroBiomarkerImage
              src={blobMediaUrl("/images/mri/hero.jpeg")}
              alt=""
              className="aspect-[567/440]"
              primary={heroReadings.primary}
              secondary={heroReadings.secondary}
              floatingImage={{
                src: "https://wvms6gve4aqx5vj4.public.blob.vercel-storage.com/images/go/hero-brain-segmentation.avif",
                alt: "Brain volumetry segmentation",
                width: 140,
              }}
            />
          </div>
        </div>
      </section>

      {/* ─── 1b. Trust features (USP bar) ─── */}
      <ValuePropCards
        labels={TRUST_ITEMS.map((key) => t(key))}
        className="!py-3 md:!py-5"
      />

      {/* ─── 2. Partner logos ─── */}
      <section className="py-8">
        <PartnerLogoMarquee />
      </section>

      {/* ─── 3. Services carousel ─── */}
      <section className="py-16 md:py-24">
        <div>
          <div className="mx-auto mb-8 flex max-w-[1440px] flex-col gap-4 px-4 md:flex-row md:items-end md:justify-between md:px-10">
            <h2 className="type-display-sm text-neutral-900">
              {ht(hk.servicesHeading)}
            </h2>
            <p className="type-body-lg max-w-[340px] text-neutral-600 md:text-right lg:max-w-[460px]">
              {ht(hk.servicesDescription)}
            </p>
          </div>
        </div>
        <ServicesCarousel
          items={translatedServices}
          learnMoreLabel={ut("learnMore")}
          disclaimerLabel={ut("illustrativeSamples")}
        />
      </section>

      {/* ─── 3b. Biomarker & disease explorer ─── */}
      <BiomarkerDiseaseExplorer
        packages={["core", "advanced", "pro"]}
        defaultTab="diseases"
      />

      {/* ─── 4. MRI pricing ─── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-10">
          <div>
            <h2 className="type-display-md text-center text-neutral-900">
              {t(k.fullbodymriPricingHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-2xl text-center text-neutral-600">
              {t(k.fullbodymriPricingBody, {
                price: prices.fullPrices.core ?? "",
              })}
            </p>
          </div>
          <div className="mt-12">
            <MriPackageCards />
          </div>
        </div>
      </section>

      {/* Quiz CTA — drives undecided users to the personalised quiz */}
      <section className="px-4 md:px-10">
        <div className="mx-auto max-w-[1440px]">
          <QuizCtaBanner variant="compact" />
        </div>
      </section>

      {/* ─── 5. Couples discount ─── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-10">
          <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-8 md:grid-cols-2 md:gap-12">
            <div>
              <h2 className="type-display-sm text-neutral-900">
                {t(k.couplesHeading)}
              </h2>
              <p className="type-body-lg mt-4 text-neutral-600">
                {t(k.couplesBody)}
              </p>
              <ul className="mt-6 space-y-3">
                {COUPLES_BULLETS.map((key) => (
                  <li key={key} className="flex items-start gap-3">
                    <CheckIcon
                      width={28}
                      height={28}
                      className="mt-0.5 flex-shrink-0"
                    />
                    <span className="type-body-md text-neutral-700">
                      {t(key)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <BookingCta
                  category="mri"
                  trackingLabel="Couples book MRI"
                  trackingLocation="fullbodymri-couples"
                  className="type-title-md bg-aubergine-800 hover:bg-aubergine-700 inline-flex items-center justify-center rounded-full px-8 py-3 text-white transition-colors"
                >
                  {mriComingSoon ? ut("joinWaitlist") : t(k.couplesCta)}
                </BookingCta>
              </div>
            </div>
            <div>
              <Image
                src={blobMediaUrl("/images/go/couples.jpg")}
                alt=""
                width={450}
                height={450}
                className="h-auto w-full rounded-[16px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── 6. Insurance pitch ─── */}
      <InsuranceSection trackingLocation="fullbodymri-insurance" />

      {/* ─── 7. Testimonials ─── */}
      <TestimonialsSection cmsTestimonials={initialTestimonials} />

      {/* ─── 7b. How it works (scroll-linked phone walkthrough) ─── */}
      <PhoneWalkthrough />

      {/* ─── 8. Mid-page CTA ─── */}
      <div>
        <CtaBanner
          title={t(k.ctaHeading)}
          subtitle={t(k.ctaBody)}
          imageSrc={blobMediaUrl("/images/homepage/cta-bottom.jpeg")}
          primaryCta={{ label: t(k.ctaCta1), href: lp("/pricing") }}
          secondaryCta={{ label: t(k.ctaCta2), href: lp("/contact") }}
        />
      </div>

      {/* ─── 10. Report preview (aka "A smarter way to understand your health") ─── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-10">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="type-display-md text-neutral-900">
              {t(k.reportHeading)}
            </h2>
            <p className="type-body-lg mt-4 text-neutral-600">
              {t(k.reportBody)}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                href={lp("/report")}
                className="type-title-md bg-aubergine-800 hover:bg-aubergine-700 inline-flex items-center justify-center rounded-full px-8 py-3 text-white transition-colors"
              >
                {t(k.reportCta1)}
              </Link>
              <Link
                href={lp("/contact")}
                className="type-title-md inline-flex items-center justify-center rounded-full border border-neutral-300 px-8 py-3 text-neutral-900 transition-colors hover:bg-neutral-900/5"
              >
                {t(k.reportCta2)}
              </Link>
            </div>
          </div>
          <div>
            <div className="mx-auto mt-12 max-w-4xl">
              <ReportMockupIpad
                explainers={{
                  selector: rpt("explainerSelector"),
                  nav: rpt("explainerNav"),
                  bioAge: rpt("explainerBioAge"),
                  heartRisk: rpt("explainerHeartRisk"),
                  biomarkers: rpt("explainerBiomarkers"),
                  findings: rpt("explainerFindings"),
                  summary: rpt("explainerSummary"),
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── 11. Expert team ─── */}
      <DoctorsSection
        initialExperts={experts}
        heading={
          <div className="mx-auto mb-10 max-w-[1440px] px-10 text-center">
            <h2 className="type-display-md text-neutral-900">
              {t(k.expertsHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-4 max-w-2xl text-neutral-600">
              {t(k.expertsSub)}
            </p>
          </div>
        }
      />

      {/* ─── 12. FAQ ─── */}
      <FaqSection />

      {/* ─── 13. Bottom CTA ─── */}
      <div>
        <CtaBanner
          title={t(k.ctaHeading)}
          subtitle={t(k.ctaBody)}
          imageSrc={blobMediaUrl("/images/homepage/cta-bottom.jpeg")}
          primaryCta={{ label: t(k.ctaCta1), href: lp("/pricing") }}
          secondaryCta={{ label: t(k.ctaCta2), href: lp("/contact") }}
        />
      </div>
    </>
  )
}
